import { useState } from 'react';
import {
    Alert,
    AlertDescription,
    AlertIcon,
    AlertTitle,
    Button,
    ListItem,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Text,
    UnorderedList,
    VStack,
} from '@chakra-ui/react';
import { CheckIcon, CloseIcon } from '@chakra-ui/icons';
import { toast } from 'react-toastify';
import { axios } from '@/services/axios';
import { useCompanyForm } from 'pages/organisations/companies/v5/hooks/useCompanyForm';
import useDeleteWholesaler from '../queries/useDeleteWholesaler';

const DeleteWholesalerModal = ({ isOpen, onClose, resetToMainTable }) => {
    const { formData } = useCompanyForm();
    const { mutate: deleteWholesaler, isLoading: isDeleting } =
        useDeleteWholesaler();
    const [isChecking, setIsChecking] = useState(false);
    const [issues, setIssues] = useState(null);

    const hasBlockingIssues = Boolean(
        issues?.numberBlocks?.message || issues?.partners?.message,
    );

    const checkDeleteEligibility = async () => {
        setIsChecking(true);
        try {
            const [numberBlocksResponse, partnersResponse] = await Promise.all([
                axios.get(`/numberblocks/${formData?.id}/`),
                axios.get(`/partners/${formData?.id}`),
            ]);

            const numberBlocks = numberBlocksResponse.data || [];
            const partners = partnersResponse.data || [];

            setIssues({
                numberBlocks: {
                    message:
                        numberBlocks.length > 0 &&
                        `There are still ${numberBlocks.length} Number Blocks assigned to this Wholesaler.`,
                    response: numberBlocks,
                },
                partners: {
                    message:
                        partners.length > 0 &&
                        `There are still ${partners.length} Partners assigned to this Wholesaler.`,
                    response: partners,
                },
            });
        } catch (error) {
            toast.error(
                error?.response?.data?.message ||
                    error?.message ||
                    'Could not check whether this wholesaler can be deleted.',
            );
        } finally {
            setIsChecking(false);
        }
    };

    const handleDelete = () => {
        if (!formData?.id || hasBlockingIssues) return;

        deleteWholesaler(formData.id, {
            onSuccess: () => {
                onClose();
                if (resetToMainTable) {
                    resetToMainTable();
                }
            },
        });
    };

    const handleClose = () => {
        setIssues(null);
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} isCentered size="lg">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Delete Wholesaler</ModalHeader>
                <ModalBody>
                    <VStack spacing={4} align="stretch">
                        <Alert status="error">
                            <AlertIcon />
                            <VStack align="start" spacing={1}>
                                <AlertTitle>
                                    Warning: this action cannot be undone.
                                </AlertTitle>
                                <AlertDescription>
                                    Deleting a Wholesaler will change invoices
                                    to finalizing and stop future billing.
                                </AlertDescription>
                            </VStack>
                        </Alert>
                        <Text>
                            Are you sure you want to delete{' '}
                            <strong>
                                {formData?.name || 'this wholesaler'}
                            </strong>
                            ?
                        </Text>
                        {issues && (
                            <UnorderedList listStyleType="none" spacing={2}>
                                <ListItem>
                                    {issues.numberBlocks.message ? (
                                        <CloseIcon color="red.500" mr={2} />
                                    ) : (
                                        <CheckIcon color="green.500" mr={2} />
                                    )}
                                    {issues.numberBlocks.message ||
                                        'There are no Number Blocks found under this Wholesaler.'}
                                </ListItem>
                                <ListItem>
                                    {issues.partners.message ? (
                                        <CloseIcon color="red.500" mr={2} />
                                    ) : (
                                        <CheckIcon color="green.500" mr={2} />
                                    )}
                                    {issues.partners.message ||
                                        'There are no Partners found under this Wholesaler.'}
                                </ListItem>
                            </UnorderedList>
                        )}
                    </VStack>
                </ModalBody>
                <ModalFooter>
                    <Button
                        variant="outline"
                        mr={3}
                        onClick={handleClose}
                        isDisabled={isDeleting || isChecking}>
                        Cancel
                    </Button>
                    {!issues ? (
                        <Button
                            colorScheme="whatsapp"
                            onClick={checkDeleteEligibility}
                            isLoading={isChecking}
                            loadingText="Checking...">
                            Check
                        </Button>
                    ) : (
                        <Button
                            colorScheme="red"
                            onClick={handleDelete}
                            isDisabled={hasBlockingIssues}
                            isLoading={isDeleting}
                            loadingText="Deleting...">
                            Delete
                        </Button>
                    )}
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default DeleteWholesalerModal;
